import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { AgeGateProvider } from "@/context/AgeGateContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";
import CartDrawer from "@/components/CartDrawer";
import { FEATURES } from "@/lib/features";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import RequireOnboarding from "@/components/RequireOnboarding";
import DashboardLayout from "@/components/DashboardLayout";
import AdminLayout from "@/components/AdminLayout";

// Public pages
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Research from "@/pages/Research"; // legacy; unused after Research Hub launch — kept to avoid touching many files
// eslint-disable-next-line no-unused-vars
const _keep_research_ref = Research;
import Quality from "@/pages/Quality";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";

// Auth
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";

// Dashboard
import DashboardOverview from "@/pages/dashboard/Overview";
import DashOrders from "@/pages/dashboard/Orders";
import DashPets from "@/pages/dashboard/Pets";
import DashAddresses from "@/pages/dashboard/Addresses";
import DashInvoices from "@/pages/dashboard/Invoices";
import DashTickets from "@/pages/dashboard/Tickets";
import DashTicketDetail from "@/pages/dashboard/TicketDetail";
import DashWishlist from "@/pages/dashboard/Wishlist";
import DashRecent from "@/pages/dashboard/RecentlyViewed";
import DashProfile from "@/pages/dashboard/Profile";
import AffiliateShell from "@/pages/dashboard/affiliate/_Shell";
import AffiliateOverview from "@/pages/dashboard/affiliate/Overview";
import AffiliateOrders from "@/pages/dashboard/affiliate/Orders";
import AffiliatePayouts from "@/pages/dashboard/affiliate/Payouts";
import AffiliatePerformance from "@/pages/dashboard/affiliate/Performance";
import AffiliateTerms from "@/pages/dashboard/affiliate/Terms";
import Onboarding from "@/pages/onboarding/Onboarding";

// Admin
import AdminOverview from "@/pages/admin/Overview";
import AdminCustomers from "@/pages/admin/Customers";
import AdminCustomerDetail from "@/pages/admin/CustomerDetail";
import AdminProducts from "@/pages/admin/Products";
import AdminInventory from "@/pages/admin/Inventory";
import AdminOrders from "@/pages/admin/Orders";
import AdminTickets from "@/pages/admin/Tickets";
import AdminTicketDetail from "@/pages/admin/TicketDetail";
import AdminCoupons from "@/pages/admin/Coupons";
import AdminNewsletter from "@/pages/admin/Newsletter";
import AdminContactMessages from "@/pages/admin/ContactMessages";
import AdminEmailTemplates from "@/pages/admin/EmailTemplates";
import AdminShipping from "@/pages/admin/Shipping";
import AdminOrderDetail from "@/pages/admin/OrderDetail";

// Checkout flow
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";

// Research Hub
import ResearchHub from "@/pages/ResearchHub";
import ResearchArticle from "@/pages/ResearchArticle";
import ResearchListing from "@/pages/ResearchListing";
import AdminResearchDashboard from "@/pages/admin/research/Dashboard";
import AdminArticles from "@/pages/admin/research/Articles";
import AdminArticleEditor from "@/pages/admin/research/ArticleEditor";
import { AdminCategories, AdminTags, AdminAuthors, AdminTemplates } from "@/pages/admin/research/Taxonomy";
import AdminMedia from "@/pages/admin/research/Media";
import { AdminAffiliatesList, AdminAffiliateDetail, AdminAffiliateApplications, AdminAffiliatePayouts, AdminAffiliateSettings, AffiliateApplyPublic } from "@/pages/admin/affiliates/index";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Public site */}
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/peptides/:slug" element={<ProductDetail />} />
        <Route path="/quality" element={<Quality />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/register" element={<Register />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
      </Route>

      {/* Legacy /portal → /dashboard */}
      <Route path="/portal" element={<Navigate to="/dashboard" replace />} />

      {/* Onboarding (protected but bypasses onboarding gate itself) */}
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

      {/* Checkout flow */}
      <Route element={<Layout />}>
        <Route path="/checkout" element={<ProtectedRoute><RequireOnboarding><Checkout /></RequireOnboarding></ProtectedRoute>} />
        <Route path="/order-success/:orderId" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
        <Route path="/research" element={<ResearchHub />} />
        <Route path="/research/category/:slug" element={<ResearchListing mode="category" />} />
        <Route path="/research/tag/:slug" element={<ResearchListing mode="tag" />} />
        <Route path="/research/:slug" element={<ResearchArticle />} />
        <Route path="/affiliates/apply" element={<AffiliateApplyPublic />} />
      </Route>

      {/* Customer dashboard */}
      <Route element={<Layout />}>
        <Route element={<ProtectedRoute><RequireOnboarding><DashboardLayout /></RequireOnboarding></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardOverview />} />
          <Route path="/dashboard/orders" element={<DashOrders />} />
          {FEATURES.pets && <Route path="/dashboard/pets" element={<DashPets />} />}
          <Route path="/dashboard/addresses" element={<DashAddresses />} />
          <Route path="/dashboard/invoices" element={<DashInvoices />} />
          <Route path="/dashboard/tickets" element={<DashTickets />} />
          <Route path="/dashboard/tickets/:id" element={<DashTicketDetail />} />
          <Route path="/dashboard/wishlist" element={<DashWishlist />} />
          <Route path="/dashboard/recently-viewed" element={<DashRecent />} />
          <Route path="/dashboard/profile" element={<DashProfile />} />
          <Route path="/dashboard/affiliate" element={<AffiliateShell />}>
            <Route index element={<AffiliateOverview />} />
            <Route path="orders" element={<AffiliateOrders />} />
            <Route path="payouts" element={<AffiliatePayouts />} />
            <Route path="performance" element={<AffiliatePerformance />} />
            <Route path="terms" element={<AffiliateTerms />} />
          </Route>
        </Route>
      </Route>

      {/* Admin panel */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/customers" element={<AdminCustomers />} />
        <Route path="/admin/customers/:id" element={<AdminCustomerDetail />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/inventory" element={<AdminInventory />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/tickets" element={<AdminTickets />} />
        <Route path="/admin/tickets/:id" element={<AdminTicketDetail />} />
        <Route path="/admin/coupons" element={<AdminCoupons />} />
        <Route path="/admin/newsletter" element={<AdminNewsletter />} />
        <Route path="/admin/contact-messages" element={<AdminContactMessages />} />
        <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
        <Route path="/admin/shipping" element={<AdminShipping />} />
        <Route path="/admin/orders/:orderId" element={<AdminOrderDetail />} />
        <Route path="/admin/research" element={<AdminResearchDashboard />} />
        <Route path="/admin/research/articles" element={<AdminArticles />} />
        <Route path="/admin/research/articles/new" element={<AdminArticleEditor />} />
        <Route path="/admin/research/articles/:slug" element={<AdminArticleEditor />} />
        <Route path="/admin/research/categories" element={<AdminCategories />} />
        <Route path="/admin/research/tags" element={<AdminTags />} />
        <Route path="/admin/research/authors" element={<AdminAuthors />} />
        <Route path="/admin/research/media" element={<AdminMedia />} />
        <Route path="/admin/research/templates" element={<AdminTemplates />} />
        <Route path="/admin/affiliates" element={<AdminAffiliatesList />} />
        <Route path="/admin/affiliates/applications" element={<AdminAffiliateApplications />} />
        <Route path="/admin/affiliates/payouts" element={<AdminAffiliatePayouts />} />
        <Route path="/admin/affiliates/settings" element={<AdminAffiliateSettings />} />
        <Route path="/admin/affiliates/:affiliateId" element={<AdminAffiliateDetail />} />
      </Route>

      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AgeGateProvider>
          <AuthProvider>
            <CartProvider>
              <AppRouter />
              <CartDrawer />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </AuthProvider>
        </AgeGateProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
